import { useState } from "react";
import { ChevronDown, ChevronRight, Lock, MessageSquare } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * GarmentNotes - Expandable notes section for garment line items
 * Supports both client-facing notes (appear on PDF) and internal notes (never on PDF)
 * 
 * @param {Object} garment - The garment object
 * @param {Function} onUpdate - Callback to update garment fields
 * @param {boolean} isAdmin - Whether the user is admin (staff can also add notes)
 */
export default function GarmentNotes({ garment, onUpdate, isAdmin }) {
  const [isExpanded, setIsExpanded] = useState(
    !!(garment.client_note || garment.internal_note)
  );
  
  const hasNotes = garment.client_note || garment.internal_note;
  const MAX_CHARS = 500;
  
  return (
    <div className="col-span-2 sm:col-span-12 mt-1">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors py-1"
        data-testid="toggle-garment-notes"
      >
        {isExpanded ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        <MessageSquare className="w-3 h-3" />
        <span>
          {hasNotes ? "Edit notes" : "Add notes"}
        </span>
        {hasNotes && !isExpanded && (
          <span className="text-gray-400 ml-1">
            ({garment.client_note ? "client" : ""}{garment.client_note && garment.internal_note ? " + " : ""}{garment.internal_note ? "internal" : ""})
          </span>
        )}
      </button>
      
      {/* Notes fields (collapsible) */}
      {isExpanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2 p-3 bg-gray-50/50 rounded-lg border border-gray-100">
          {/* Client Note */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3 text-blue-500" />
              Client Note
              <span className="text-gray-400 font-normal">(appears on PDF)</span>
            </Label>
            <Textarea
              placeholder="e.g., Price includes one-colour print on front..."
              value={garment.client_note || ""}
              onChange={(e) => {
                const value = e.target.value.slice(0, MAX_CHARS);
                onUpdate("client_note", value);
              }}
              className="text-sm min-h-[60px] resize-none bg-white"
              maxLength={MAX_CHARS}
              data-testid="garment-client-note"
            />
            <div className="text-xs text-gray-400 text-right">
              {(garment.client_note || "").length}/{MAX_CHARS}
            </div>
          </div>
          
          {/* Internal Note */}
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-1.5">
              <Lock className="w-3 h-3 text-amber-500" />
              Internal Note
              <span className="text-amber-600 font-normal">(never on PDF)</span>
            </Label>
            <Textarea
              placeholder="e.g., Use supplier X for this order, check stock levels..."
              value={garment.internal_note || ""}
              onChange={(e) => {
                const value = e.target.value.slice(0, MAX_CHARS);
                onUpdate("internal_note", value);
              }}
              className="text-sm min-h-[60px] resize-none bg-amber-50/50 border-amber-200"
              maxLength={MAX_CHARS}
              data-testid="garment-internal-note"
            />
            <div className="text-xs text-gray-400 text-right">
              {(garment.internal_note || "").length}/{MAX_CHARS}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
